import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { CheckRounded, ExpandMoreOutlined } from "@mui/icons-material";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import HeadlessButton from "./Button";

export default function HeadlessCombobox({
  value,
  ref,
  name,
  options,
  pageSize = 5,
  field,
  helpers,
  errorMsg,
  className,
  placeholder,
  onOptionChange,
  disabled = false,
  onChange,
  ...restProps
}) {
  const [selectedOption, setSelectedOption] = useState(value || null);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    setSelectedOption(value || null);
  }, [value]);

  useEffect(() => {
    setPage(1);
  }, [pageSize, query]);

  const filteredOptions = useMemo(() => {
    if (query === "") {
      return options;
    } else {
      return options.filter((option) => {
        return option.value.toLowerCase().includes(query.toLowerCase());
      });
    }
  }, [options, query]);

  const totalPages = useMemo(
    () => (pageSize > 0 ? Math.ceil(filteredOptions.length / pageSize) : 1),
    [filteredOptions.length, pageSize]
  );

  const optionsForPage = useMemo(() => {
    if (pageSize === 0) {
      return filteredOptions;
    }
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredOptions.slice(startIndex, endIndex);
  }, [filteredOptions, page, pageSize]);

  return (
    <Combobox
      value={selectedOption}
      disabled={disabled}
      onChange={(value) => {
        setSelectedOption(value);
        onOptionChange && onOptionChange(value);
        helpers && helpers.setValue(value?.value);
      }}
      onClose={() => setQuery("")}
    >
      <div className="tw-relative">
        <ComboboxInput
          className={clsx(
            errorMsg &&
              "tw-ring-2 tw-border-none tw-outline-none tw-ring-error-light",
            "tw-w-full tw-rounded-md tw-border tw-border-boarder-light tw-py-1 tw-pr-8 tw-pl-2 tw-h-9 tw-text-text-primary-light",
            "tw-focus:outline-none tw-focus:border-none data-[focus]:tw-ring-2",
            disabled ? "tw-bg-disabled-bg-light tw-cursor-not-allowed" : "",
            errorMsg
              ? "data-[focus]:tw-ring-error-light"
              : "data-[focus]:tw-ring-focus"
          )}
          displayValue={(option) => option?.value}
          onChange={(event) => {
            setQuery(event.target.value);
            helpers && helpers.setValue(event.target.value);
            setSelectedOption(null);
          }}
          onBlur={(event) => {
            field && field.onBlur(event);
          }}
          ref={ref}
          name={name}
          id={name}
          {...restProps}
        />

        <ComboboxButton
          className="tw-group tw-absolute tw-inset-y-0 tw-right-0 tw-px-2.5"
          data-testid="combo-button"
        >
          <ExpandMoreOutlined className="tw-size-4 tw-fill-text-primary-light group-data-[hover]:tw-fill-boarder-light" />
        </ComboboxButton>

        {pageSize > 0 && optionsForPage.length > 0 && (
          <ComboboxOptions
            transition
            className={clsx(
              "tw-absolute tw-mt-1 tw-z-10 tw-w-full tw-rounded-md tw-border tw-border-boarder-light tw-bg-white tw-p-1 [--anchor-gap:4px] ",
              "tw-transition tw-duration-100 tw-ease-in data-[leave]:data-[closed]:tw-opacity-0"
            )}
          >
            {optionsForPage.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option}
                className="tw-group tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-py-1.5 tw-px-3 tw-select-none data-[focus]:tw-bg-disabled-bg-light"
              >
                <CheckRounded className="tw-invisible tw-size-4 tw-fill-white group-data-[selected]:tw-visible" />
                <div className="tw-text-sm/6 tw-text-text-primary-light">
                  {option.value}
                </div>
              </ComboboxOption>
            ))}
            <div className="tw-flex tw-justify-between tw-items-center tw-py-2 tw-px-4">
              <HeadlessButton
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                theme="none"
              >
                {t("Combobox.previous")}
              </HeadlessButton>

              <span className="tw-text-text-primary-light">
                {t("Combobox.pages", { page: page, total: totalPages })}
              </span>

              <HeadlessButton
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                theme="none"
              >
                {t("Combobox.next")}
              </HeadlessButton>
            </div>
          </ComboboxOptions>
        )}
        {pageSize === 0 && optionsForPage.length > 0 && (
          <ComboboxOptions
            transition
            className={clsx(
              "tw-absolute tw-mt-1 tw-z-10 tw-w-full tw-rounded-md tw-border tw-border-boarder-light tw-bg-white tw-p-1 [--anchor-gap:4px] tw-max-h-[200px] tw-overflow-y-auto ",
              "tw-transition tw-duration-100 tw-ease-in data-[leave]:data-[closed]:tw-opacity-0"
            )}
          >
            {optionsForPage.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option}
                className="tw-group tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-py-0.5 tw-px-3 tw-select-none data-[focus]:tw-bg-disabled-bg-light"
              >
                <CheckRounded className="tw-invisible tw-size-4 tw-fill-white group-data-[selected]:tw-visible" />
                <div className="tw-text-sm/6 tw-text-text-primary-light">
                  {option.value}
                </div>
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
}
